import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import PricingPlans from './components/PricingPlans';
import CurrentSubscription from './components/CurrentSubscription';
import FeatureComparison from './components/FeatureComparison';
import BillingHistory from './components/BillingHistory';
import PaymentMethods from './components/PaymentMethods';
import UsageAnalytics from './components/UsageAnalytics';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const PremiumSubscriptionManagement = () => {
  const [activeTab, setActiveTab] = useState('plans');
  const [currentPlan, setCurrentPlan] = useState('free');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState({
    planType: 'free',
    renewalDate: '2025-10-07',
    usage: {
      profileViews: 1250,
      messagesSent: 45,
      searchQueries: 78,
      analyticsAccess: false
    },
    features: {
      unlimitedMessaging: false,
      prioritySupport: false,
      advancedAnalytics: false,
      profileBoost: false,
      exclusiveEvents: false
    }
  });

  const tabs = [
    { id: 'plans', label: 'Formules', icon: 'Package' },
    { id: 'current', label: 'Mon abonnement', icon: 'CreditCard' },
    { id: 'features', label: 'Comparaison', icon: 'CheckSquare' },
    { id: 'billing', label: 'Facturation', icon: 'Receipt' },
    { id: 'usage', label: 'Utilisation', icon: 'BarChart3' }
  ];

  useEffect(() => {
    // Simulate loading subscription data
    const loadSubscriptionData = async () => {
      try {
        setIsLoading(true);
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Mock subscription data - would come from API
        const mockData = {
          planType: 'professional',
          renewalDate: '2025-10-07',
          usage: {
            profileViews: 2847,
            messagesSent: 156,
            searchQueries: 234,
            analyticsAccess: true
          },
          features: {
            unlimitedMessaging: true,
            prioritySupport: true,
            advancedAnalytics: true,
            profileBoost: true,
            exclusiveEvents: false
          }
        };
        
        setSubscriptionData(mockData);
        setCurrentPlan(mockData?.planType);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading subscription data:', error);
        setIsLoading(false);
      }
    };

    loadSubscriptionData();
  }, []);

  const getCurrentTime = () => {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })?.format(new Date());
  };

  const handlePlanUpgrade = (newPlan) => {
    console.log(`Upgrading to ${newPlan} plan`);
    // Here you would integrate with payment processing
    setCurrentPlan(newPlan);
    setActiveTab('current');
  };

  const handleBillingCycleChange = (cycle) => {
    setBillingCycle(cycle);
    console.log(`Billing cycle changed to ${cycle}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
              <p className="text-muted-foreground">Chargement de votre abonnement...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'plans':
        return (
          <PricingPlans
            currentPlan={currentPlan}
            billingCycle={billingCycle}
            onPlanSelect={handlePlanUpgrade}
            onBillingCycleChange={handleBillingCycleChange}
          />
        );
      case 'current':
        return (
          <CurrentSubscription
            subscriptionData={subscriptionData}
            onPlanUpgrade={handlePlanUpgrade}
          />
        );
      case 'features':
        return (
          <FeatureComparison
            currentPlan={currentPlan}
            subscriptionData={subscriptionData}
          />
        );
      case 'billing':
        return (
          <div className="space-y-8">
            <BillingHistory currentPlan={currentPlan} />
            <PaymentMethods />
          </div>
        );
      case 'usage':
        return (
          <UsageAnalytics
            subscriptionData={subscriptionData}
            currentPlan={currentPlan}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Gestion Premium
                </h1>
                <p className="text-muted-foreground">
                  {getCurrentTime()} • Optimisez votre présence artistique
                </p>
              </div>
              <div className="mt-4 sm:mt-0 flex items-center space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="HelpCircle"
                  iconPosition="left"
                  iconSize={16}
                  onClick={() => console.log('Open help center')}
                >
                  Aide
                </Button>
                {currentPlan === 'free' && (
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Crown"
                    iconPosition="left"
                    iconSize={16}
                    onClick={() => setActiveTab('plans')}
                  >
                    Passer Premium
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Current Plan Status Banner */}
          {currentPlan !== 'free' && (
            <div className="mb-8 bg-gradient-to-r from-accent/10 via-primary/10 to-accent/10 border border-accent/20 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-accent rounded-lg">
                    <Icon name="Crown" size={24} color="white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground capitalize">
                      Plan {currentPlan}
                    </h3>
                    <p className="text-muted-foreground">
                      Renouvellement automatique le {new Intl.DateTimeFormat('fr-FR')?.format(new Date(subscriptionData?.renewalDate))}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                  <span className="text-success font-medium">Actif</span>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Tabs */}
          <div className="mb-8">
            <div className="border-b border-border overflow-x-auto">
              <nav className="flex space-x-8 min-w-max">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'border-accent text-accent' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[600px]">
            {renderTabContent()}
          </div>

          {/* Value Proposition Section */}
          <div className="mt-16 bg-gradient-to-br from-primary/5 to-accent/5 rounded-lg p-8 border border-primary/10">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Pourquoi choisir Artis.io Premium ?
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Rejoignez une communauté exclusive d'artistes professionnels et développez votre carrière avec nos outils avancés
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mx-auto mb-4">
                  <Icon name="TrendingUp" size={24} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Visibilité Maximale
                </h3>
                <p className="text-muted-foreground text-sm">
                  Apparaissez en priorité dans les résultats de recherche et boostez votre profil
                </p>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-accent/10 rounded-full mx-auto mb-4">
                  <Icon name="Users" size={24} className="text-accent" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Réseau Exclusif
                </h3>
                <p className="text-muted-foreground text-sm">
                  Accédez à des événements privés et connectez-vous avec l'élite artistique
                </p>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mx-auto mb-4">
                  <Icon name="BarChart3" size={24} className="text-success" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Analyses Avancées
                </h3>
                <p className="text-muted-foreground text-sm">
                  Obtenez des insights détaillés sur votre performance et votre audience
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                © {new Date()?.getFullYear()} Artis.io. Plateforme dédiée aux artistes professionnels.
              </p>
              <div className="mt-4 sm:mt-0 flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Shield"
                  iconPosition="left"
                  iconSize={14}
                  onClick={() => console.log('View security')}
                >
                  Sécurité
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="FileText"
                  iconPosition="left"
                  iconSize={14}
                  onClick={() => console.log('View terms')}
                >
                  Conditions
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="MessageSquare"
                  iconPosition="left"
                  iconSize={14}
                  onClick={() => console.log('Contact support')}
                >
                  Support Premium
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PremiumSubscriptionManagement;